import { createContext, ReactNode, useContext, useRef, useState } from "react";

interface ContextType {
  top: number;
  right: number;
  updateBirdPosition: (top: number, right: number) => void;
}

const AnimatedBirdContext = createContext<ContextType>({
  top: 3,
  right: 30,
  updateBirdPosition: () => {},
});

interface IBirdPosition {
  top: number;
  right: number;
}

const initialPosition = { top: 3, right: 30 };

// Function to generate the first point (other than A) on the line segment from A to B,
// which is at least 1 cm away from point A and in the direction towards B
function generateFirstPointInDirectionTowardsB(
  AX: number,
  AY: number,
  BX: number,
  BY: number
) {
  let distanceAB = Math.sqrt(Math.pow(BX - AX, 2) + Math.pow(BY - AY, 2));

  // If the distance is less than the specified distance, return point A
  if (distanceAB < 3) {
    return { right: AX, top: AY };
  }

  // Calculate the direction vector from A to B
  let directionX = (BX - AX) / distanceAB;
  let directionY = (BY - AY) / distanceAB;

  // Calculate the coordinates of the point at the specified distance from A
  let pointX = AX + directionX * 3;
  let pointY = AY + directionY * 3;

  return { right: pointX, top: pointY };
}

export const AnimatedBirdProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState(initialPosition);

  const timeoutRef = useRef<null | NodeJS.Timeout>(null);

  const handleUpdatePosition = (
    currentVal: IBirdPosition,
    val: IBirdPosition
  ) => {
    if (!(currentVal.right === val.right && currentVal.top === val.top)) {
      const newVal = generateFirstPointInDirectionTowardsB(
        currentVal.right,
        currentVal.top,
        val.right,
        val.top
      );
      setState(newVal);
      timeoutRef.current = setTimeout(() => {
        handleUpdatePosition(newVal, val);
      }, 1);
    }
  };

  const clearBirdTimeout = () => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
  };

  const updateBirdPosition = (right: number, top: number) => {
    clearBirdTimeout();
    if (!(state.right === right && state.top === top)) {
      clearBirdTimeout();
      handleUpdatePosition(state, { top, right });
    }
  };

  return (
    <AnimatedBirdContext.Provider
      value={{
        top: state.top,
        right: state.right,
        updateBirdPosition,
      }}
    >
      {children}
    </AnimatedBirdContext.Provider>
  );
};

export const useAnimatedBird = () => useContext(AnimatedBirdContext);
